import { useEffect, useState } from "react";
import { useAuth } from "react-oidc-context";
import { useNavigate } from "react-router-dom";
import { LoaderCircle } from "lucide-react";
import Button from "../components/shared/Button";
import {
  clearSessionRejections,
  isSessionRejectionLoop,
} from "../lib/sessionRejectionGuard";

export default function LoginPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  // Evaluated once per visit: the API kept rejecting fresh tokens, so another
  // automatic redirect would only bounce straight back here.
  const [rejectionLoop] = useState(() => isSessionRejectionLoop());

  // Already authenticated – go straight to the app.
  useEffect(() => {
    if (auth.isAuthenticated) {
      navigate("/", { replace: true });
    }
  }, [auth.isAuthenticated, navigate]);

  // Not authenticated and idle – kick off the PKCE redirect automatically so
  // the user never has to click a button.
  useEffect(() => {
    if (
      !rejectionLoop &&
      !auth.isAuthenticated &&
      !auth.isLoading &&
      !auth.activeNavigator
    ) {
      void auth.signinRedirect();
    }
  }, [auth, rejectionLoop]);

  const retry = () => {
    clearSessionRejections();
    void auth.signinRedirect();
  };

  // Only render UI when there is an error; otherwise the user never sees this
  // page – they are transparently redirected to Zitadel.
  if (auth.error || rejectionLoop) {
    return (
      <div className="w-full h-screen flex flex-col items-center justify-center bg-neutral-900 text-white p-4">
        <div className="w-full max-w-sm text-center p-8 bg-neutral-800/80 backdrop-blur-sm rounded-xl border border-neutral-700/80 shadow-2xl space-y-4">
          <p className="text-sm text-rose-400">
            {auth.error
              ? auth.error.message
              : "We could not verify your session with the server. Please try again in a moment."}
          </p>
          <Button
            variant="primary"
            size="lg"
            className="w-full"
            onClick={retry}
          >
            Try again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-screen flex items-center justify-center bg-neutral-900">
      <LoaderCircle className="animate-spin h-8 w-8 text-neutral-400" />
    </div>
  );
}
